import { Card, Progress, Tag, Typography } from 'antd';
import { FileTextOutlined, GlobalOutlined } from '@ant-design/icons';
import {
  useBillingSummary,
  useIngestionStatus,
  useScrapeProgress,
  type SourceListItem,
  type SourceStatus,
} from '@entities/source';

const { Text } = Typography;

interface Props {
  source: SourceListItem;
  isSelected: boolean;
  onSelect: () => void;
}

/**
 * Карточка одного источника в левой панели.
 *
 * Поведение по типу источника:
 *  - Документ (PDF/DOCX/etc): polling через useIngestionStatus,
 *    показывает компактный мигающий прогресс-бар для in_progress
 *  - Сайт (имя начинается с http/https): дополнительно polling через
 *    useScrapeProgress — показывает «X / Y страниц» + точный прогресс,
 *    плюс отдельная строка «N картинок» (без знаменателя — он плавает)
 *
 * Завершённые источники (success/partial_success):
 *  - Подтягиваем billing-summary для показа итоговой суммы списания
 *    («Списано X₽») под статусом
 *
 * Эвристика isWebsite пока по имени (regex). Когда бэк начнёт отдавать
 * source.type — заменим.
 */
export function SourceCard({ source, isSelected, onSelect }: Props) {
  const isWebsite = isWebsiteSource(source.name);

  const isPolling =
    source.status === 'in_queue' ||
    source.status === 'in_progress' ||
    source.status === 'pending_confirm';

  // Базовый статус для всех типов
  const { data: live } = useIngestionStatus(isPolling ? source.source_id : undefined);
  const effectiveStatus: SourceStatus = live?.status ?? source.status;

  // Для веб-источников дополнительно поллим scrape progress.
  const shouldPollScrapeProgress = isWebsite && isPolling;
  const { data: scrapeProgress } = useScrapeProgress(
    shouldPollScrapeProgress ? source.source_id : undefined,
  );

  // Для завершённых источников подтягиваем сумму списания.
  // Не делаем для in_progress — данные ещё не финальные.
  const isTerminal =
    effectiveStatus === 'success' ||
    effectiveStatus === 'partial_success' ||
    effectiveStatus === 'fail';
  const { data: billing } = useBillingSummary(source.source_id, isTerminal);

  return (
    <Card
      size="small"
      hoverable
      onClick={onSelect}
      styles={{ body: { padding: 12 } }}
      style={{
        marginBottom: 8,
        cursor: 'pointer',
        borderColor: isSelected ? '#1677ff' : undefined,
        borderWidth: isSelected ? 2 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flexShrink: 0, color: '#1677ff', fontSize: 16, marginTop: 2 }}>
          {isWebsite ? <GlobalOutlined /> : <FileTextOutlined />}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 13,
              color: '#1A1A1A',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginBottom: 4,
            }}
            title={source.name ?? 'Без имени'}
          >
            {source.name ?? 'Без имени'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StatusTag status={effectiveStatus} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatDate(source.created_at)}
            </Text>
          </div>

          {/* Прогресс для активных */}
          {effectiveStatus === 'in_progress' && (
            <>
              {isWebsite && scrapeProgress ? (
                <WebProgress progress={scrapeProgress} />
              ) : (
                <Progress
                  percent={50}
                  size="small"
                  showInfo={false}
                  status="active"
                  style={{ marginTop: 6, marginBottom: 0 }}
                />
              )}
            </>
          )}

          {/* Списано X₽ для завершённых (новое в p4-fix-v2) */}
          {isTerminal && billing && parseFloat(billing.total_rub) > 0 && (
            <Text
              type="secondary"
              style={{ fontSize: 11, display: 'block', marginTop: 4 }}
            >
              Списано: {parseFloat(billing.total_rub).toFixed(2)} ₽
            </Text>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Прогресс веб-источника в карточке (X/Y страниц + картинки + ETA). */
function WebProgress({
  progress,
}: {
  progress: import('@entities/source').ScrapeProgress;
}) {
  const eta = formatEta(progress.eta_seconds);
  return (
    <div style={{ marginTop: 6 }}>
      <Progress
        percent={progress.pages_percent}
        size="small"
        showInfo={false}
        status="active"
        style={{ marginBottom: 4 }}
      />
      <Text type="secondary" style={{ fontSize: 11 }}>
        {progress.pages_done} / {progress.pages_total} страниц
        {progress.images_done > 0 && <> · {progress.images_done} картинок</>}
        {eta && <> · {eta}</>}
      </Text>
    </div>
  );
}

function isWebsiteSource(name: string | null): boolean {
  if (!name) return false;
  return /^https?:\/\//i.test(name);
}

interface StatusTagProps {
  status: SourceStatus;
}

function StatusTag({ status }: StatusTagProps) {
  const config = STATUS_CONFIG[status];
  return (
    <Tag color={config.color} bordered={false} style={{ fontSize: 11, margin: 0 }}>
      {config.label}
    </Tag>
  );
}

const STATUS_CONFIG: Record<SourceStatus, { label: string; color: string }> = {
  pending_confirm: { label: 'Ждёт подтверждения', color: 'default' },
  in_queue: { label: 'В очереди', color: 'default' },
  in_progress: { label: 'Обработка...', color: 'blue' },
  success: { label: 'Готов', color: 'success' },
  partial_success: { label: 'Частично', color: 'warning' },
  fail: { label: 'Ошибка', color: 'error' },
};

function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatEta(seconds: number | null): string | null {
  if (seconds === null || seconds < 60) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `~${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const restMin = minutes % 60;
  if (restMin === 0) return `~${hours} ч`;
  return `~${hours} ч ${restMin} мин`;
}
